import React, { useEffect, useState } from 'react';

export interface AnchorLink {
  title: string;
  id: string;
}

interface TableOfContentsProps {
  links: AnchorLink[];
}

export default function TableOfContents({ links }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Detect current active anchor
  useEffect(() => {
    const headings = links.map((link) => document.getElementById(link.id)).filter((a) => !!a);
    const visibilityThreshold = 200; // Pixels from top to consider "active"

    const handleScroll = () => {
      const scrollPosition = window.scrollY + visibilityThreshold;

      const activeHeading = headings.reduce((lastActive: string | null, heading) => {
        return heading.offsetTop <= scrollPosition ? heading.id : lastActive;
      }, null);

      setActiveId(activeHeading);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [links]);

  // Handle scroll to anchor
  const handleClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const HEADER_HEIGHT = 97;
      const MARGIN_FROM_HEADER = 20;
      const yOffset = -(HEADER_HEIGHT + MARGIN_FROM_HEADER);
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });

      window.history.pushState(null, '', `#${id}`); // Update URL without jumping
    }
  };

  if (links.length === 0) {
    return null;
  }

  return (
    <div className="table-of-contents">
      {links?.map((link, idx) => (
        <a
          key={idx}
          className={activeId === link.id ? 'active' : ''}
          href={`#${link.id}`}
          onClick={(e) => handleClick(e, link.id)}>
          {link.title}
        </a>
      ))}
    </div>
  );
}
